import React, { useEffect, useState, FormEvent } from "react";
import { Modal, Form, Spinner } from "react-bootstrap";
import { isMobile } from "react-device-detect";
import {
  projectPrimaryColor,
  projectTertiaryColor,
} from "../../generalConfigs";
import { GiRunningNinja, GiWalk, GiCoffeeCup, GiRocket } from "react-icons/gi";
import { parseAssetTypeInt } from "../../utils";
import { useHistory } from "react-router-dom";
import useIcons from "../../hooks/useIcons";
import useProfilePicture from "../../hooks/useProfilePicture";
import useAssets from "../../hooks/useAssets";
import { useUser } from "../../hooks/useUser";
import useAuth from "../../hooks/useAuth";
import { RiImageAddLine } from "react-icons/ri";
import { sampleSize } from "lodash";
import Swal from "sweetalert2";
import GradientText from "../../components/Logo";

type UserSelectedPreference = {
  investorType: number;
  profileImage: any;
  assets: any[];
};

export default function Welcome() {
  const [userData, setUserData] = useState<string | undefined>();
  const [investorProfiles, setInvestorProfiles] = useState(
    defaultInvestorProfiles
  );
  const [profilePictures, setProfilePictures] = useState(
    defaultProfilePictures
  );
  const { getSuggestions } = useAssets();
  const randomSuggestions = getSuggestions(5);
  const [assetsToFollow, setAssetsToFolow] = useState(randomSuggestions);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [userSelectedPreferences, setUserSelectedPreferences] =
    useState<UserSelectedPreference>({
      investorType: 1,
      profileImage: null,
      assets: [],
    });
  const [errorMsg, setErrorMsg] = useState<string>("");
  const [step, setStep] = useState<number>(1);
  const [isLoadingUpload, setIsLoadingUpload] = useState<boolean>(false);
  const [uploadedFileName, setUploadedImageName] = useState<string>("");
  const [file, setFile] = useState<File | null | undefined>(null);

  const history = useHistory();
  const { getURI } = useIcons();
  const { getUserDetails, storeFirstSteps, uploadProfileImage } = useUser();
  const { redirectUnauthenticatedUser } = useAuth();

  useEffect(() => {
    redirectUnauthenticatedUser();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  /**
   * This effect receives the user data and determines if he can continue to welcome page -
   * or redirect to dashboard.
   */

  useEffect(() => {
    async function fetchData() {
      setIsLoading(true);

      const userDetailsResponse = await getUserDetails();

      if (userDetailsResponse && userDetailsResponse.status === 1) {
        //if user already selected the investor Type.
        if (userDetailsResponse.data.investor_type !== null) {
          history.push("/dashboard");
        } else {
          setIsLoading(false);
          setUserData(JSON.stringify(userDetailsResponse.data));
        }
      } else {
        setUserData("error");
        setIsLoading(false);
      }
    }

    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [history]);

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();

    const lastStep = 3;
    if (step === lastStep) {
      setIsLoading(true);
      let { investorType, profileImage, assets } = userSelectedPreferences;

      //Add anon image if user not selected any image
      if (!profileImage) {
        profileImage = "anonymous-profile-image.png";
      }

      //Send data.
      const storeResponse = await storeFirstSteps({
        investorType: investorType,
        profileImage: profileImage,
        assets: JSON.stringify(assets),
      });

      if (storeResponse) {
        if (storeResponse.status === 1) {
          return history.push("/how-to-use");
        } else {
          setIsLoading(false);
          setErrorMsg(
            "Ocorreu um erro desconhecido, por favor, verifique sua seleção e tente novamente."
          );
        }
      } else {
        setIsLoading(false);
        setErrorMsg("Ocorreu um erro desconhecido, por favor, tente novamente");
      }
    }
  };

  async function handleImageUpload() {
    if (!file) {
      return;
    }
    setIsLoadingUpload(true);

    const response = await uploadProfileImage(file);

    if (response) {
      const msg = response.msg;

      if (msg === "Uploaded successfully" && response.fileName) {
        setUploadedImageName(response.fileName);
        handleSelectProfilePicture(response.fileName);
      } else if (msg === "image not acceptable") {
        Swal.fire({
          icon: "error",
          title: "Imagem incompatível.",
          confirmButtonColor: projectPrimaryColor,
          text: "Atualmente aceitamos apenas imagens nos seguintes formatos: jpeg, jpg, png. Com um tamanho máximo de até 7MB, certifique de selecionar uma imagem que atenda esses critérios.",
          background: "var(--bg-1)",
          color: "var(--text-1)",
        });
      }
    } else {
      Swal.fire({
        icon: "error",
        title: "Oops...",
        text: "Ocorreu um erro desconhecido, que tal tentar novamente?!",
        confirmButtonColor: projectPrimaryColor,
        background: "var(--bg-1)",
        color: "var(--text-1)",
      });
    }

    setIsLoadingUpload(false);
  }

  //Upload profile image
  useEffect(() => {
    if (file) {
      handleImageUpload();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [file]);

  function handleFileChange(event: React.ChangeEvent<HTMLInputElement>) {
    if (isLoadingUpload) {
      return;
    }
    event.preventDefault();
    const file = event?.target?.files?.[0];

    if (event.target.files) {
      setFile(file);
    }
  }

  /**
   * This function handles the selected investor type by the user
   * Change the state of selecteds checkboxs and change the userSelectedPreferences.
   */
  const handleInvestorType = (selectedInvestorType: number) => {
    let newProfile = investorProfiles.map((item) => {
      if (item.investorType === selectedInvestorType) {
        item.status = true;
        return item;
      }
      item.status = false;
      return item;
    });

    setInvestorProfiles(newProfile);
    setUserSelectedPreferences({
      investorType: selectedInvestorType,
      profileImage: userSelectedPreferences.profileImage,
      assets: userSelectedPreferences.assets,
    });
  };

  /**
   * This function handle the profile picture selection by the user
   */
  const handleSelectProfilePicture = (selected: string) => {
    let newProfilePictures = profilePictures.map((item) => {
      if (item.identifier === selected) {
        item.status = true;
        return item;
      } else {
        item.status = false;
        return item;
      }
    });

    setProfilePictures(newProfilePictures);
    setUserSelectedPreferences({
      investorType: userSelectedPreferences.investorType,
      profileImage: selected,
      assets: userSelectedPreferences.assets,
    });
  };

  const handleAssetsFollow = (assetId: string) => {
    let newData = assetsToFollow.map((item) => {
      if (item.id === assetId) {
        if (item.status === true) {
          item.status = false;
        } else {
          item.status = true;
        }
        return item;
      }
      return item;
    });
    setAssetsToFolow(newData);

    //New user selected preferences.
    let newAssetPreference = assetsToFollow.map((item) => {
      if (item.status) {
        return item.id;
      }
      return null;
    });

    setUserSelectedPreferences({
      investorType: userSelectedPreferences.investorType,
      profileImage: userSelectedPreferences.profileImage,
      assets: newAssetPreference.filter((x) => x !== null),
    });
  };

  if (isLoading) {
    return loadingSkeleton();
  } else if (userData === "error") {
    return fatalError();
  }
  //User investor type selection
  if (step === 1) {
    return (
      <Modal size="lg" fullscreen={true} show={true}>
        <div
          style={{
            borderRadius: "0 15px 15px 0",
            backgroundColor: "var(--bg-9)",
          }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-6 shadow-lg">
                <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                  <div className="hero-mask opacity-9 bg-primary" />
                  <div className="hero-bg hero-bg-scroll" />
                  <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                    <div className="row g-0">
                      <div className="col-11 col-lg-10 mx-auto">
                        <div className="mt-3 az-logo">
                          <GradientText text={"FatoresAI"} />
                        </div>
                      </div>
                    </div>
                    <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                      <div className="col-11 col-lg-10 mx-auto">
                        <h1
                          className="text-11"
                          style={{ color: "var(--white)" }}
                        >
                          Parabéns, agora você é{" "}
                          <span className="markText-animation">fato!</span>
                          🎉
                        </h1>
                        <span style={{ color: "var(--white)" }}>
                          Seja muito bem-vindo a melhor plataforma de análises
                          de ativos, é uma honra te ter como usuário, vamos
                          finalizar as configurações iniciais? É rapidinho.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="col-lg-6 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
                style={{
                  backgroundColor: "var(--bg-6)",
                  borderRadius: 10,
                }}
              >
                <div className="container my-auto py-4">
                  <div className="row">
                    <div className="col-11 col-lg-10 mx-auto">
                      <h3 className="text-black mb-3 text-center">
                        Configurações iniciais
                      </h3>
                      <p className="tx-14 text-center">
                        Selecione o perfil de investidor que mais combina com
                        você.
                      </p>

                      <Form style={{ marginTop: 15 }}>
                        {investorProfiles.map((item, i) => (
                          <div key={i} className="mb-3">
                            <span
                              onClick={() =>
                                handleInvestorType(item.investorType)
                              }
                            >
                              <Form.Check type={"radio"}>
                                <Form.Check.Input
                                  type={"radio"}
                                  isValid
                                  onChange={() =>
                                    handleInvestorType(item.investorType)
                                  }
                                  checked={item.status}
                                />
                                <Form.Check.Label className="tx-medium">
                                  {item.title} {item.icon}
                                </Form.Check.Label>
                                <br />
                                <span>{item.description}</span>
                                <Form.Control.Feedback type="valid">
                                  Normalmente indicado para {item.indicatedFor}
                                </Form.Control.Feedback>
                              </Form.Check>
                            </span>
                          </div>
                        ))}
                        <div className="centered-flex">
                          <button
                            className="btn btn-primary"
                            style={{
                              borderRadius: 10,
                              width: "50%",
                              backgroundColor: projectPrimaryColor,
                            }}
                            type="button"
                            onClick={() => setStep(2)}
                          >
                            Próximo
                          </button>
                        </div>

                        <div className="centered-flex mg-t-10">
                          <span
                            style={{
                              color: "var(--text-0)",
                              fontSize: 10,
                              textAlign: "center",
                            }}
                          >
                            Você pode alternar de perfil de investidor a
                            qualquer momento nas configurações.
                          </span>
                        </div>
                      </Form>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  } //User profile picture selection
  else if (step === 2) {
    const quote = getQuoteAboutInvestorType(
      userSelectedPreferences.investorType
    );
    return (
      <Modal size="lg" fullscreen={true} show={true}>
        <div
          style={{
            borderRadius: "0 15px 15px 0",
            backgroundColor: "var(--bg-9)",
          }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-6 shadow-lg">
                <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                  <div className="hero-mask opacity-9 bg-primary" />
                  <div className="hero-bg hero-bg-scroll" />
                  <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                    <div className="row g-0">
                      <div className="col-11 col-lg-10 mx-auto">
                        <div className="mt-3 az-logo">
                          <GradientText text={"FatoresAI"} />
                        </div>
                      </div>
                    </div>
                    <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                      <div className="col-11 col-lg-10 mx-auto">
                        <h1
                          className="text-11"
                          style={{ color: "var(--white)" }}
                        >
                          {quote.title}{" "}
                          <span className="markText-animation">
                            {quote.highlightWord}
                          </span>
                        </h1>
                        <span className="text-white">{quote.subtitle}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="col-lg-6 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
                style={{
                  backgroundColor: "var(--bg-6)",
                  borderRadius: 10,
                }}
              >
                <div className="container my-auto py-4">
                  <div className="row">
                    <div className="col-11 col-lg-10 mx-auto">
                      <h3 className="text-black mb-3 text-center">
                        Configurações iniciais
                      </h3>
                      <p className="tx-14 text-center">
                        Se quiser, selecione um avatar ou envie uma imagem para
                        utilizar na plataforma.
                      </p>

                      <div className="row">
                        {profilePictures.map((img, i) => {
                          return (
                            <div className="col-6" key={i}>
                              <div
                                className="az-header-profile"
                                onClick={() =>
                                  handleSelectProfilePicture(img.identifier)
                                }
                              >
                                <div
                                  className="az-img-user"
                                  style={{
                                    border: img.status
                                      ? "5px solid " + projectPrimaryColor
                                      : "3px solid gray",
                                    cursor: "pointer",
                                  }}
                                >
                                  <img src={img.link} alt="" />
                                </div>
                              </div>
                            </div>
                          );
                        })}

                        {/* Select image from computer */}
                        {!uploadedFileName || isLoadingUpload ? (
                          <div className="col-6">
                            <div className="az-header-profile">
                              <label
                                htmlFor={!isLoadingUpload ? "file-input" : ""}
                              >
                                <div
                                  className="az-img-user d-flex"
                                  style={{
                                    border: false
                                      ? "5px solid " + projectPrimaryColor
                                      : "3px solid gray",
                                    cursor: isLoadingUpload
                                      ? "default"
                                      : "pointer",
                                    backgroundColor: "#343640",
                                  }}
                                >
                                  <div
                                    className="centered-flex"
                                    style={{ margin: "auto" }}
                                  >
                                    {isLoadingUpload ? (
                                      <Spinner
                                        color="#ffffff"
                                        animation="border"
                                      />
                                    ) : (
                                      <RiImageAddLine size={40} color="white" />
                                    )}
                                  </div>
                                  <input
                                    onChange={(e) => handleFileChange(e)}
                                    id="file-input"
                                    className="d-none"
                                    type="file"
                                  />
                                </div>
                              </label>
                            </div>
                          </div>
                        ) : (
                          // Uploaded profile image
                          <div className="col-6">
                            <div
                              className="az-header-profile"
                              onClick={() =>
                                handleSelectProfilePicture(uploadedFileName)
                              }
                            >
                              <label htmlFor="file-input">
                                <div
                                  className="az-img-user"
                                  style={{
                                    border:
                                      userSelectedPreferences.profileImage ===
                                        uploadedFileName
                                        ? "4px solid " + projectPrimaryColor
                                        : "3px solid gray",
                                    cursor: "pointer",
                                  }}
                                >
                                  <img
                                    src={getProfilePictureURI(uploadedFileName)}
                                    alt=""
                                  />
                                </div>

                                <input
                                  onChange={(e) => handleFileChange(e)}
                                  id="file-input"
                                  className="d-none"
                                  type="file"
                                />
                              </label>
                            </div>
                          </div>
                        )}
                      </div>
                      <div className="centered-flex">
                        <button
                          type="button"
                          className="btn btn-primary"
                          style={{
                            borderRadius: 10,
                            width: "50%",
                            backgroundColor: projectPrimaryColor,
                          }}
                          onClick={() => setStep(3)}
                        >
                          {userSelectedPreferences.profileImage
                            ? "Próximo"
                            : "Pular"}
                        </button>
                      </div>

                      <div className="centered-flex">
                        <button
                          className="btn btn-primary"
                          type="button"
                          style={{
                            borderRadius: 10,
                            width: "30%",
                            marginTop: 10,
                            backgroundColor: "gray",
                          }}
                          onClick={() => setStep(1)}
                        >
                          Voltar
                        </button>
                      </div>

                      <div className="centered-flex mg-t-20">
                        <span
                          style={{
                            color: "var(--text-0)",
                            fontSize: 10,
                            textAlign: "center",
                          }}
                        >
                          Você pode mudar isso a qualquer momento nas
                          configurações.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  } //User Follow assets selection
  else if (step === 3) {
    return (
      <Modal size="lg" fullscreen={true} show={true}>
        <div
          style={{
            borderRadius: "0 15px 15px 0",
            backgroundColor: "var(--bg-9)",
          }}
        >
          <div className="container">
            <div className="row">
              <div className="col-lg-6 shadow-lg">
                <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                  <div className="hero-mask opacity-9 bg-primary" />
                  <div className="hero-bg hero-bg-scroll" />
                  <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                    <div className="row g-0">
                      <div className="col-12 col-lg-10 mx-auto">
                        <div className="mt-3 az-logo">
                          <GradientText text={"FatoresAI"} />
                        </div>
                      </div>
                    </div>
                    <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                      <div className="col-11 col-lg-10 mx-auto">
                        <h1 className="text-11" style={{ color: "#e0e0e0" }}>
                          Que <span className="markText-animation">tal?</span>
                        </h1>
                        <span className="text-white">
                          Você pode seguir alguns ativos e acompanhar eles
                          diretamente em seu painel, se quiser você pode seguir
                          agora ou depois.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              <div
                className="col-lg-6 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
                style={{
                  backgroundColor: "var(--bg-6)",
                  borderRadius: 10,
                }}
              >
                <div className="container my-auto py-4">
                  <div className="row">
                    <div className="col-12 mx-auto">
                      <h3 className="text-black mb-3 text-center">Sugestões</h3>
                      <p className="tx-14 text-center">
                        Você pode começar a seguir alguns ativos e ter sempre
                        novidades e visões rápidas em seu painel.
                      </p>

                      <Form style={{ marginTop: 15 }} onSubmit={handleSubmit}>
                        <div>
                          <div className="wd-lg-100p">
                            <ul className="list-group wd-md-100p">
                              {assetsToFollow.map((asset, i) => {
                                return (
                                  <li
                                    className="list-group-item d-flex align-items-center"
                                    style={{ borderRadius: 10, marginTop: 5 }}
                                    key={i}
                                  >
                                    <img
                                      src={getURI(asset.icon)}
                                      className="wd-30 rounded-circle mg-r-15"
                                      alt=""
                                    />
                                    <div>
                                      <h6 className="tx-13 tx-semibold mg-b-0">
                                        {asset.name}
                                      </h6>
                                      <span className="d-block tx-11 text-muted">
                                        {parseAssetTypeInt(asset.type)}
                                        {asset.exchange &&
                                          " (" + asset.exchange + ")"}
                                      </span>
                                    </div>
                                    <div
                                      style={{
                                        padding: 2,
                                        marginLeft: "auto",
                                      }}
                                    >
                                      <button
                                        className="btn btn-rounded btn-sm"
                                        type="button"
                                        onClick={() =>
                                          handleAssetsFollow(asset.id)
                                        }
                                        style={{
                                          backgroundColor: asset.status
                                            ? "gray"
                                            : projectTertiaryColor,
                                          color: "white",
                                        }}
                                      >
                                        {asset.status ? "Seguindo" : "Seguir"}
                                      </button>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                        <div className="centered-flex">
                          {errorMsg !== "" && (
                            <span
                              className="my-2"
                              style={{
                                color: "red",
                                textAlign: "center",
                              }}
                            >
                              {errorMsg}
                            </span>
                          )}
                        </div>
                        <div className="centered-flex">
                          <button
                            className="btn btn-primary"
                            style={{
                              borderRadius: 10,
                              width: "50%",
                              backgroundColor: projectPrimaryColor,
                              marginTop: 10,
                            }}
                            type="submit"
                          >
                            Finalizar
                          </button>
                        </div>

                        <div className="centered-flex">
                          <button
                            className="btn btn-primary"
                            style={{
                              borderRadius: 10,
                              width: "30%",
                              marginTop: 10,
                              backgroundColor: "gray",
                            }}
                            onClick={() => setStep(2)}
                            type="button"
                          >
                            Voltar
                          </button>
                        </div>
                      </Form>
                      <div className="centered-flex mg-t-20">
                        <span
                          style={{
                            color: "var(--text-0)",
                            fontSize: 10,
                            textAlign: "center",
                          }}
                        >
                          Você pode mudar isso a qualquer momento nas
                          configurações.
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </Modal>
    );
  }
}

function loadingSkeleton() {
  return (
    <div>
      <Modal size="xl" fullscreen={true} show={true}>
        <div className="d-flex justify-content-center pd-20">
          <Spinner animation="border" />
        </div>
      </Modal>
    </div>
  );
}

function fatalError() {
  return (
    <Modal size="lg" fullscreen={true} show={true}>
      <div className="bg-dark">
        <div className="container">
          <div className="row">
            <div className="col-lg-6 shadow-lg">
              <div className="hero-wrap d-flex align-items-center rounded-3 rounded-end-0 h-100">
                <div className="hero-mask opacity-9 bg-primary" />
                <div className="hero-bg hero-bg-scroll" />
                <div className="hero-content mx-auto w-100 h-100 d-flex flex-column">
                  <div className="row g-0">
                    <div className="col-11 col-lg-10 mx-auto">
                      <div className="mt-3 az-logo">
                        <GradientText text={"FatoresAI"} />
                      </div>
                    </div>
                  </div>
                  <div className={isMobile ? "row g-0 my-3" : "row g-0 my-4"}>
                    <div className="col-11 col-lg-10 mx-auto">
                      <h1 className="text-11" style={{ color: "#e0e0e0" }}>
                        <span className="markText-animation">Oops!</span>
                      </h1>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div
              className="col-lg-6 shadow-lg d-flex align-items-center rounded-3 rounded-start-0"
              style={{
                backgroundColor: "white",
                borderRadius: 10,
              }}
            >
              <div className="container my-auto py-4">
                <div className="row">
                  <div className="col-11 col-lg-10 mx-auto">
                    <div className="centered-flex">
                      {<GiRocket size={32} />}
                      <h4>Houston, temos um problema!</h4>
                      <span>
                        Infelizmente, alguns dados não aterrissaram da maneira
                        correta.
                        <br />
                        <a href={"?"}>Clique aqui para tentar novamente.</a>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Modal>
  );
}

/**
 * This function generates an quote about the selected investor type.
 * we will use it in the second step.
 */
const getQuoteAboutInvestorType = (selectedInvestorType: number) => {
  const quote = {
    title: "",
    highlightWord: "",
    subtitle: "",
  };

  switch (selectedInvestorType) {
    case 0:
      quote.title = "Olhos";
      quote.highlightWord = "abertos!";
      quote.subtitle =
        "Você selecionou o perfil 'Agressivo', fique sempre alerta em suas operações e mantenha um bom gerenciamento de risco, a Lucy vai estar ao seu lado para te auxiliar em suas decisões.";
      break;
    case 1:
      quote.title = "Moderação é";
      quote.highlightWord = "essencial.";
      quote.subtitle =
        "Você selecionou o perfil 'Moderado', ótimo, a Lucy ama moderação e vai te auxiliar muito bem na sua jornada de investimentos.";
      break;
    case 2:
      quote.title = "Sabedoria constrói";
      quote.highlightWord = "riqueza.";
      quote.subtitle =
        "Você selecionou o perfil 'Conservador', excelente, a pressa é inimiga da perfeição, a Lucy vai te auxiliar muito bem em sua jornada de construção de patrimônio!";
      break;
    default:
      quote.title = "Falta";
      quote.highlightWord = "pouco";
      quote.subtitle =
        "Selecione uma foto de perfil para se identificar na plataforma";
      break;
  }
  return quote;
};

const defaultInvestorProfiles = [
  {
    title: "Agressivo",
    investorType: 0,
    description:
      "Realiza diversas operações visando curto prazo com duração de horas ou dias.",
    indicatedFor: "Day Traders",
    icon: <GiRunningNinja />,
    status: false,
  },
  {
    title: "Moderado",
    investorType: 1,
    description:
      "Realiza operações visando médio prazo com duração de semanas ou meses.",
    indicatedFor: "Swing Traders",
    icon: <GiWalk />,
    status: true,
  },
  {
    title: "Conservador",
    investorType: 2,
    description:
      "Realiza operações visando longo prazo com duração de meses ou anos.",
    indicatedFor: "Buy and Holders",
    icon: <GiCoffeeCup />,
    status: false,
  },
];

// eslint-disable-next-line react-hooks/rules-of-hooks

function getThreeRandomIdentifiers(): string[] {
  const allImages = [
    "beer_red.png",
    "bull_green.png",
    "cat_universe.png",
    "lhamel_flys.png",
    "monkey_astronault.png",
    "the_light.png",
  ];

  return sampleSize(allImages, 3);
}

// eslint-disable-next-line react-hooks/rules-of-hooks
const { getProfilePictureURI } = useProfilePicture();

const threeRandomIdentifiers = getThreeRandomIdentifiers();
const defaultProfilePictures = [
  {
    identifier: threeRandomIdentifiers[0],
    link: getProfilePictureURI(threeRandomIdentifiers[0]),
    status: false,
  },
  {
    identifier: threeRandomIdentifiers[1],
    link: getProfilePictureURI(threeRandomIdentifiers[1]),
    status: false,
  },
  {
    identifier: threeRandomIdentifiers[2],
    link: getProfilePictureURI(threeRandomIdentifiers[2]),
    status: false,
  },
];
